import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Smartphone, Mail, Clock, ArrowUpRight } from "lucide-react";

export default function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-300 relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #C5A059 0%, transparent 40%), radial-gradient(circle at 80% 60%, #C5A059 0%, transparent 40%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-10">
        {/* Massive brand title */}
        <div className="border-b border-white/10 pb-12 mb-12">
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold leading-none tracking-tighter text-white">
            Akaydın <span className="text-brand-gold">Döviz</span>
          </h2>
          <p className="mt-4 text-brand-gold text-sm font-semibold tracking-wide uppercase">
            B Grubu Yetkili Müessese
          </p>
          <p className="mt-2 text-slate-400 text-sm">
            Akaydın Döviz Ve Altın Ticareti Sınırlı Yetkili Müessese A.Ş.
          </p>
          <p className="mt-4 text-slate-400 max-w-2xl text-base">
            Yalova'nın güvenilir finans noktası. Yıllardır şeffaf, hızlı ve güvenli döviz hizmeti sunuyoruz.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Address */}
          <div className="md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
              Adres & Konum
            </div>
            <div className="flex gap-4 mb-6">
              <MapPin className="w-5 h-5 text-brand-gold shrink-0 mt-1" />
              <p className="text-base text-white leading-relaxed">
                Fevzi Çakmak Mahallesi,<br />
                İstanbul Caddesi No:4,<br />
                77100 Merkez/Yalova
              </p>
            </div>
            <div className="rounded-xl overflow-hidden border border-white/10 aspect-[16/9]">
              <iframe
                title="Akaydın Döviz Konum"
                src="https://maps.google.com/maps?q=Akayd%C4%B1n+D%C3%B6viz&ll=40.6556461,29.2710678&z=17&output=embed"
                className="w-full h-full grayscale-[20%]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
              İletişim
            </div>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+902268112950"
                  className="flex items-center gap-3 text-white hover:text-brand-gold transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 text-brand-gold" />
                  +90 226 811 29 50
                </a>
              </li>
              <li>
                <a
                  href="tel:+905400804977"
                  className="flex items-center gap-3 text-white hover:text-brand-gold transition-colors"
                  data-testid="footer-mobile"
                >
                  <Smartphone className="w-4 h-4 text-brand-gold" />
                  0540 080 49 77
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@akaydindoviz.com"
                  className="flex items-center gap-3 text-slate-300 hover:text-brand-gold transition-colors"
                >
                  <Mail className="w-4 h-4 text-brand-gold" />
                  info@akaydindoviz.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <Clock className="w-4 h-4 text-brand-gold mt-1" />
                <div className="text-sm">
                  Pzt-Cmt: 09:00 - 18:30<br />
                  Pazar: Kapalı
                </div>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
              Kurumsal
            </div>
            <ul className="space-y-3">
              {[
                ["/hakkimizda", "Hakkımızda"],
                ["/hesap-numaralari", "Hesap Numaralarımız"],
                ["/sirket-profili", "Şirket Profili"],
                ["/misyon-vizyon", "Misyon & Vizyon"],
                ["/hizmetler", "Hizmetlerimiz"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-sm text-slate-300 hover:text-brand-gold transition-colors flex items-center gap-1.5 group"
                  >
                    {label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
              Hizmetler
            </div>
            <ul className="space-y-3">
              {[
                ["/cevirici", "Döviz Çevirici"],
                ["/", "Anlık Kurlar"],
                ["/haberler", "Haberler"],
                ["/blog", "Blog"],
                ["/iletisim", "İletişim"],
              ].map(([to, label]) => (
                <li key={label}>
                  <Link
                    to={to}
                    className="text-sm text-slate-300 hover:text-brand-gold transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Akaydın Döviz Ve Altın Ticareti Sınırlı Yetkili Müessese A.Ş. Tüm hakları saklıdır.
          </p>
          <p className="text-xs text-slate-500">
            Made by{" "}
            <a
              href="https://vegola.com.tr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-gold font-semibold hover:underline"
              data-testid="footer-credit-vegola"
            >
              Vegola.
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
